import { inject } from '@angular/core';
import { Channel, ChannelMemberResponse, FormatMessageResponse, MessageResponse } from 'stream-chat';
import { DateFormats, formatDateToString } from '@core';
import { ChatChannelType } from '@core-http';
import { STREAM_CHAT_CLIENT_TOKEN, StreamChatClientProvider } from '../../../../infrastructure/tokens';
import { isSameDay } from 'date-fns';

type ChannelMessage = MessageResponse | FormatMessageResponse | undefined;

const CHANNEL_TYPE_ICONS: Readonly<Partial<Record<ChatChannelType, string>>> = {
  [ChatChannelType.multi]: 'assets/icons/chat-game-channel.svg',
  [ChatChannelType.team]: 'assets/icons/chat-group-channel.svg'
};

export class ChannelListItemViewService {
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);

  getChannelName(channel: Channel): string {
    const data: Record<string, unknown> | undefined = channel.data as Record<string, unknown> | undefined;
    const channelName: string | undefined = data?.['name'] as string | undefined;

    if (channelName) return channelName;

    const otherMember: ChannelMemberResponse | undefined = this.getOtherMember(channel);
    return (otherMember?.user?.name as string) || 'Chat';
  }

  getChannelImage(channel: Channel): string | null {
    const data: Record<string, unknown> | undefined = channel.data as Record<string, unknown> | undefined;
    const channelImage: string | undefined = data?.['image'] as string | undefined;

    if (channelImage) return channelImage;

    const otherMember: ChannelMemberResponse | undefined = this.getOtherMember(channel);
    return (otherMember?.user?.image as string) || null;
  }

  getLastMessage(channel: Channel): string {
    const lastMsg: ChannelMessage = channel.state?.messages?.slice(-1)[0];
    if (!lastMsg) return '';
    const text: string = lastMsg.text || '';
    return text.length > 35 ? text.substring(0, 35) + '...' : text;
  }

  getLastMessageTime(channel: Channel): string {
    const lastMsg: ChannelMessage = channel.state?.messages?.slice(-1)[0];
    if (!lastMsg?.created_at) return '';

    const date: Date = new Date(lastMsg.created_at);
    const format: DateFormats = isSameDay(date, new Date()) ? DateFormats.Time : DateFormats.DayMonthDot;
    return formatDateToString(date, format);
  }

  getUnreadCount(channel: Channel): number {
    return channel.state?.unreadCount || 0;
  }

  isMuted(channel: Channel): boolean {
    return channel.muteStatus()?.muted || false;
  }

  getChannelTypeIcon(channel: Channel): string | null {
    return CHANNEL_TYPE_ICONS[channel.type as ChatChannelType] ?? null;
  }

  private getOtherMember(channel: Channel): ChannelMemberResponse | undefined {
    const currentUserId: string | undefined = this.chatClientProvider.getCurrentUserId();
    const members: ChannelMemberResponse[] = Object.values(channel.state?.members || {});
    return members.find((member: ChannelMemberResponse) => member.user_id !== currentUserId);
  }
}
